import React, { useCallback } from 'react'
import classNames from 'classnames'
import { Case, Switch } from 'react-if'
import { GuildScore } from '@/atoms/GuildScore'
import { Image } from '@/atoms/Image'
import { CaptionLG, CaptionMD } from '@/atoms/Text'
import { paths } from '@/constants/paths'
import { useIntersectionObserver } from '@/hooks/useIntersectionObserver'
import {
  SuggestedRailItemInteraction,
  useIsolatedCardInteractions,
  useImpressionTracking,
} from '@/services/Analytics/ContentInteractions'
import { RelatedTitleContentV2 } from '@/types/codegen-federation'
import { getCloudinaryImageUrl } from '@/utils/Cloudinary'
import { useSafeTrack } from '@/utils/analytics'
import { Translate, useTranslate } from '@/utils/translate/translate-client'

export interface SuggestedContentCardProps {
  recommendedItem?: RelatedTitleContentV2
  itemIndex?: number
  className?: string
  pagePlacement?: number
}

export const SuggestedContentCard: React.FC<SuggestedContentCardProps> = ({
  recommendedItem,
  itemIndex = 0,
  className,
  pagePlacement = 0,
}) => {
  const { t } = useTranslate('watch')
  const track = useSafeTrack()
  const [cardRef, isVisible] = useIntersectionObserver<HTMLDivElement>({
    threshold: 0.9,
  })

  const contentId = recommendedItem?.title?.id ?? ''

  const createInteraction = useCallback(
    (item: RelatedTitleContentV2) => {
      if (!item.title?.id || !item.title?.image?.cloudinaryPath) return null

      return new SuggestedRailItemInteraction({
        id: item.title.id,
        __typename: item.title.__typename ?? null,
        attribution: item.attribution,
      })
        .withSurface('brand_landing')
        .withPlacement('suggested_tab', pagePlacement, itemIndex, 'horizontal_swimlane')
        .withImageArtwork(item.title.image.cloudinaryPath, 'portrait_display')
    },
    [pagePlacement, itemIndex],
  )

  const { handleMouseEnter, handleMouseLeave, handleClick } = useIsolatedCardInteractions({
    contentId,
    item: recommendedItem!,
    itemIndex,
    createInteraction,
    onClickTrack: () =>
      track('Suggested Shows and Films Item Clicked', {
        projectSlug: recommendedItem?.title?.project?.slug ?? '',
        context: 'Watch Project Page',
      }),
  })

  useImpressionTracking({
    isVisible,
    contentId,
    item: recommendedItem!,
    itemIndex,
    createInteraction,
  })

  if (!recommendedItem || !recommendedItem?.title?.image?.cloudinaryPath) return null

  return (
    <div
      ref={cardRef}
      aria-label="suggested-content-card"
      id={recommendedItem?.id ?? ''}
      className="relative min-w-[250px] pr-6 lg:min-w-[316px]"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className={classNames('flex flex-col', className)}>
        {recommendedItem?.title?.image?.cloudinaryPath && (
          <a
            aria-label={recommendedItem.title?.name ?? ''}
            href={`${paths.watch.index}/${recommendedItem?.title?.project?.slug}`}
            className="aspect-h-3 aspect-w-2 relative mb-3 w-full cursor-pointer overflow-hidden rounded-2xl transition-transform duration-300 ease-in-out hover:scale-[1.03]"
            onClick={handleClick}
          >
            <div className="absolute inset-0">
              <Image
                alt={recommendedItem?.title?.name ?? ''}
                src={getCloudinaryImageUrl({ path: recommendedItem?.title?.image?.cloudinaryPath })}
                width={316}
                height={474}
                className="h-full w-full object-cover"
              />
            </div>
          </a>
        )}
        <div className="flex flex-col gap-y-1">
          <CaptionMD color="core-gray-500">
            <Switch>
              <Case condition={recommendedItem?.title?.__typename === 'ContentSeries'}>
                <Translate i18nKey="series" t={t}>
                  Series
                </Translate>
              </Case>
              <Case condition={recommendedItem?.title?.__typename === 'ContentMovie'}>
                <Translate i18nKey="movie" t={t}>
                  Movie
                </Translate>
              </Case>
              <Case condition={recommendedItem?.title?.__typename === 'ContentSpecial'}>
                <Translate i18nKey="special" t={t}>
                  Special
                </Translate>
              </Case>
            </Switch>
          </CaptionMD>
          <CaptionLG weight="bold">{recommendedItem?.title?.name}</CaptionLG>
          {recommendedItem?.title?.project?.highestScore ? (
            <GuildScore guildScore={recommendedItem?.title?.project?.highestScore} />
          ) : null}
        </div>
      </div>
    </div>
  )
}
